'use client'

import { useEffect, useRef } from 'react'
import { flushSync } from 'react-dom'
import type { RefObject } from 'react'

/** Desplazamiento mínimo (px) para que un pulsado cuente como gesto y no como
 * click: por debajo, el click sobre una carátula la activa con normalidad. */
const SWIPE_THRESHOLD = 30

/** Pausa de seguridad para quitar `data-settling` si `transitionend` no llega
 * (p. ej. `prefers-reduced-motion`, sin transición que terminar). */
const SETTLE_FALLBACK_MS = 400

/** Ancho de un paso del carril (carátula + gap), medido entre las dos primeras
 * carátulas; con menos de dos, el ancho del propio carril. */
function measureStep(rail: HTMLElement): number {
  const [first, second] = rail.children
  if (first instanceof HTMLElement && second instanceof HTMLElement) {
    return second.offsetLeft - first.offsetLeft
  }
  return rail.clientWidth
}

/**
 * Arrastre con ratón sobre el rail de carátulas del HeroCarousel.
 *
 * El rail no es desplazable (`overflow: hidden`, siempre empieza por la
 * carátula activa y envuelve cíclicamente), así que `useDragScroll` no aplica:
 * aquí el arrastre se traduce en cambiar de slide. Arrastrar hacia la
 * izquierda avanza y hacia la derecha retrocede, tantas carátulas como ancho
 * se haya arrastrado (mínimo una al superar `SWIPE_THRESHOLD`).
 *
 * Mientras se arrastra, las carátulas siguen al ratón vía la variable CSS
 * `--rail-drag` (la consume `.railCard` con `translate`, que se compone aparte
 * del `transform` de hover/activa). El desplazamiento cuenta desde que se
 * supera el umbral, para que las carátulas no peguen un tirón al empezar.
 *
 * Al soltar, el arrastre casi nunca coincide con un número exacto de
 * carátulas: cambiar de slide sin más haría saltar el rail la fracción que
 * falta. Se aplica FLIP: se cambia la slide de forma síncrona (`flushSync`,
 * el rail se repinta empezando por la nueva activa), se fija `--rail-drag` al
 * desfase que mantiene cada carátula exactamente donde se soltó y, con
 * `data-settling` (habilita la transición de `translate` en CSS), se anima
 * hasta 0.
 *
 * Solo con `pointerType === 'mouse'`, igual que `useDragScroll`. Tras un
 * gesto se anula el `click` siguiente (fase de captura) para no activar la
 * carátula sobre la que se suelta, y se cancela el `dragstart` nativo de las
 * imágenes.
 *
 * `onSwipe` recibe los pasos con signo (`+n` avanzar, `-n` retroceder); se
 * guarda en un ref para no reenlazar los listeners en cada render.
 */
export function useRailSwipe(
  railRef: RefObject<HTMLElement | null>,
  onSwipe: (steps: number) => void,
): void {
  const onSwipeRef = useRef(onSwipe)
  useEffect(() => {
    onSwipeRef.current = onSwipe
  }, [onSwipe])

  useEffect(() => {
    const el = railRef.current
    if (!el) return

    let pointerId: number | null = null
    let startX = 0
    let swiping = false

    const onPointerDown = (event: PointerEvent): void => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return
      pointerId = event.pointerId
      startX = event.clientX
      swiping = false
    }

    let settleTimer: ReturnType<typeof setTimeout> | undefined

    const endSettle = (): void => {
      clearTimeout(settleTimer)
      el.removeAttribute('data-settling')
    }

    const resetDrag = (): void => {
      el.style.removeProperty('--rail-drag')
      el.style.cursor = ''
    }

    /** Desplazamiento a aplicar, descontando el umbral (sin tirón inicial). */
    const offsetFrom = (clientX: number): number => {
      const dx = clientX - startX
      return dx - Math.sign(dx) * SWIPE_THRESHOLD
    }

    const onPointerMove = (event: PointerEvent): void => {
      if (event.pointerId !== pointerId) return
      if (!swiping) {
        if (Math.abs(event.clientX - startX) < SWIPE_THRESHOLD) return
        // Captura solo al superar el umbral: tomarla en `pointerdown`
        // redirigiría el `click` al rail y las carátulas dejarían de activarse.
        swiping = true
        endSettle()
        el.setPointerCapture(event.pointerId)
        el.style.cursor = 'grabbing'
      }
      el.style.setProperty('--rail-drag', `${offsetFrom(event.clientX)}px`)
    }

    const onPointerUp = (event: PointerEvent): void => {
      if (event.pointerId !== pointerId) return
      pointerId = null
      if (!swiping) return
      if (el.hasPointerCapture(event.pointerId)) el.releasePointerCapture(event.pointerId)
      el.style.cursor = ''

      const offset = offsetFrom(event.clientX)
      const step = measureStep(el)
      const count = Math.max(1, Math.round(Math.abs(offset) / step))
      // Arrastrar a la izquierda (offset < 0) avanza.
      const steps = offset < 0 ? count : -count

      // FLIP: repintar ya con la nueva slide y compensar con el desfase que
      // deja cada carátula donde se soltó (`steps` carátulas más a la derecha
      // en el nuevo orden), luego animar hasta 0.
      flushSync(() => onSwipeRef.current(steps))
      el.style.setProperty('--rail-drag', `${offset + steps * step}px`)
      void el.offsetWidth // fuerza el layout con el desfase antes de animar
      el.setAttribute('data-settling', '')
      el.style.removeProperty('--rail-drag')
      clearTimeout(settleTimer)
      settleTimer = setTimeout(endSettle, SETTLE_FALLBACK_MS)
    }

    const onPointerCancel = (event: PointerEvent): void => {
      if (event.pointerId !== pointerId) return
      pointerId = null
      swiping = false
      resetDrag()
    }

    const onClickCapture = (event: MouseEvent): void => {
      if (!swiping) return
      swiping = false
      event.preventDefault()
      event.stopPropagation()
    }

    const onDragStart = (event: DragEvent): void => {
      event.preventDefault()
    }

    const onTransitionEnd = (event: TransitionEvent): void => {
      if (event.propertyName === 'translate') endSettle()
    }

    el.addEventListener('pointerdown', onPointerDown)
    el.addEventListener('pointermove', onPointerMove)
    el.addEventListener('pointerup', onPointerUp)
    el.addEventListener('pointercancel', onPointerCancel)
    el.addEventListener('click', onClickCapture, true)
    el.addEventListener('dragstart', onDragStart)
    el.addEventListener('transitionend', onTransitionEnd)

    return () => {
      el.removeEventListener('pointerdown', onPointerDown)
      el.removeEventListener('pointermove', onPointerMove)
      el.removeEventListener('pointerup', onPointerUp)
      el.removeEventListener('pointercancel', onPointerCancel)
      el.removeEventListener('click', onClickCapture, true)
      el.removeEventListener('dragstart', onDragStart)
      el.removeEventListener('transitionend', onTransitionEnd)
      clearTimeout(settleTimer)
    }
  }, [railRef])
}
